import { referenceOf, reorderGroups, type LibraryGroup, type NodeType } from '@dispositio/core';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { de } from '../../i18n/de';
import { nearestFreePoint } from '../../lib/addable';
import { categorySections, designFoci, type EntityCard, type Focus, type FocusKey } from '../../lib/designModel';
import { CATEGORY_COLORS, groupsOfType, layoutLibrary } from '../../lib/libraryModel';
import { iconFor, iconNamed } from '../../lib/toolIcons';
import { templateFor, type EntityKey } from '../../lib/tools';
import { useStoryEnv } from '../../state/storyEnv';
import { useWorkspace } from '../../state/workspace';
import { Chevron } from '../editor/BinderIcon';
import { FieldsForm } from '../fields/FieldsForm';
import { LibraryCanvas, type LibraryHandle } from '../library/LibraryCanvas';
import { LibraryImport } from '../library/LibraryImport';
import { NewGroupDialog } from '../library/NewGroupDialog';
import { MaskIcon, RailItem, SideRail, useRailCollapsed } from '../SideRail';
import { FieldAdder } from './FieldAdder';
import { FieldMenu } from './FieldMenu';
import { useCategoryActions } from './useCategories';
import { AssetImg } from '../AssetImg';
import { ReferenceIcon } from '../ReferenceIcon';
import { useDeleteActions } from '../sidebar/useDeleteActions';
import { onDeleteKey } from '../../lib/deleteKey';

const ENTITY_OF: Partial<Record<FocusKey, EntityKey>> = { person: 'person', place: 'place', landscape: 'landscape', object: 'object', organization: 'organization', concept: 'concept' };

/** Medientyp, unter dem eine Karte beim Ziehen übergeben wird */
const ENTITY_MIME = 'application/x-dispositio-entity';
/** Medientyp, unter dem eine Kategorie (Abschnitt der Kartenansicht) beim Umsortieren übergeben wird */
const CATEGORY_MIME = 'application/x-dispositio-category';
/** Kartengrößen der Kartenansicht (Mindestbreite in px); gemerkt im Browser */
const CARD_SIZES = [120, 150, 190, 240, 300];
const SIZE_KEY = 'dispositio.cardSize';

/** Zuletzt gewählte Darstellung (Karten oder Pinnwand), gilt für alle Bereiche */
let lastBoard = false;
/** Zugeklappte Kategorien der Kartenansicht */
const closedCategories = new Set<string>();

/** Icon eines Bereichs in der Farbe der Oberfläche (als Maske, damit es im hellen wie im dunklen Modus passt). */
export function FocusIcon({ focus, size = 22 }: { focus: Pick<Focus, 'key' | 'color'>; size?: number }) {
  const key = ENTITY_OF[focus.key];
  const url = key ? iconFor(key) : focus.key === 'world' ? iconNamed('world') : null;
  if (!url) return <span className="design-dot" style={{ background: focus.color }} aria-hidden="true" />;
  return <MaskIcon url={url} size={size} />;
}

const initials = (title: string): string =>
  title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

/**
 * Karte eines Eintrags: Klick (oder Enter) zeigt ihn in der Seitenleiste, Ziehen legt ihn in eine Kategorie. In einer Serie trägt eine
 * Referenz ein kleines Symbol (`reference`: die Episode ihres Originals).
 */
function Card({
  card,
  color,
  dragging,
  selected,
  reference,
  onOpen,
  onDelete,
  onDragStart,
  onDragEnd,
}: {
  card: EntityCard;
  color: string;
  dragging: boolean;
  selected: boolean;
  reference?: number | null;
  onOpen: () => void;
  /** Entf auf der Karte (mit Rückfrage) */
  onDelete: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const used = card.scenes > 0;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={`entity-card${dragging ? ' dragging' : ''}${selected ? ' selected' : ''}`}
      style={{ '--c': color } as CSSProperties}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        } else onDeleteKey(onDelete)(e);
      }}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid="entity-card"
      data-entity-id={card.entity.id}
      title={de.design.cardHint}
    >
      <span className="ec-image">{card.imageId ? <AssetImg id={card.imageId} alt="" loading="lazy" draggable={false} /> : <span className="ec-initial">{initials(card.title)}</span>}</span>
      <span className="ec-body">
        <span className="ec-title">
          {card.title}
          {reference != null && (
            <span className="ec-ref">
              <ReferenceIcon title={de.series.reference(reference)} />
            </span>
          )}
        </span>
        {card.text && <span className="ec-text">{card.text}</span>}
        <span className={`ec-usage${used ? '' : ' unused'}`}>
          {used ? de.design.scenes(card.scenes) : de.design.unused}
        </span>
      </span>
    </div>
  );
}

/** Kartengröße (−/+), gemerkt im Browser */
function useCardSize(): [number, (n: number) => void] {
  const [size, setSize] = useState(() => {
    try {
      const raw = localStorage.getItem(SIZE_KEY);
      const n = raw === null ? NaN : Number(raw);
      return Number.isInteger(n) && n >= 0 && n < CARD_SIZES.length ? n : 2;
    } catch {
      return 2;
    }
  });
  const set = (n: number): void => {
    const v = Math.max(0, Math.min(CARD_SIZES.length - 1, n));
    setSize(v);
    try {
      localStorage.setItem(SIZE_KEY, String(v));
    } catch {
      // ohne Speicher im Browser gilt die Größe nur bis zum Neuladen
    }
  };
  return [size, set];
}

/** Farbwahl einer Kategorie: kleine Palette am Farbpunkt */
function ColorDot({ color, label, onPick }: { color: string; label: string; onPick: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [open]);
  return (
    <span className="cat-color" ref={host}>
      <button type="button" className="cat-dot" style={{ background: color }} aria-label={label} title={label} aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="category-color" />
      {open && (
        <span className="cat-palette" role="listbox" aria-label={label}>
          {CATEGORY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="option"
              aria-selected={c === color}
              className={`swatch-btn${c === color ? ' on' : ''}`}
              style={{ background: c }}
              aria-label={c}
              onClick={() => {
                onPick(c);
                setOpen(false);
              }}
            />
          ))}
        </span>
      )}
    </span>
  );
}

interface CategoryDrag {
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  /** Alt+↑ / Alt+↓: eine Stelle nach oben bzw. unten */
  onMove: (delta: -1 | 1) => void;
}

/**
 * Kopf eines Abschnitts der Kartenansicht: Kategorie mit Farbe, Name (Doppelklick benennt um), Anzahl, „+“ und Löschen. Die Kopfzeile
 * lässt sich ziehen, um die Reihenfolge zu ändern (oder Alt+↑/↓ auf dem Namen).
 */
function CategoryHead({ group, count, open, newLabel, onToggle, onNew, drag }: { group: LibraryGroup; count: number; open: boolean; newLabel: string; onToggle: () => void; onNew: () => void; drag: CategoryDrag }) {
  const { actions } = useStoryEnv();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const commit = (): void => {
    setEditing(false);
    const next = name.trim();
    if (next && next !== group.name) void actions.updateGroup(group.id, { name: next });
    else setName(group.name);
  };
  const rename = (): void => {
    setName(group.name);
    setEditing(true);
  };
  /** Löschen (✕ und Entf im Kopf), mit Rückfrage – die Einträge bleiben */
  const remove = async (): Promise<void> => {
    if (!confirm(de.design.categoryDeleteConfirm(group.name, count))) return;
    await actions.deleteGroup(group.id);
  };
  return (
    <header className="cat-head" draggable={!editing} onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd} onKeyDown={onDeleteKey(editing ? null : () => void remove())} data-testid="category-head">
      <span className="cat-grip" title={de.design.categoryGrip} aria-hidden="true">
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
          <circle cx="2.5" cy="3" r="1.4" />
          <circle cx="7.5" cy="3" r="1.4" />
          <circle cx="2.5" cy="8" r="1.4" />
          <circle cx="7.5" cy="8" r="1.4" />
          <circle cx="2.5" cy="13" r="1.4" />
          <circle cx="7.5" cy="13" r="1.4" />
        </svg>
      </span>
      <Chevron open={open} onToggle={onToggle} label={open ? de.design.collapse(group.name) : de.design.expand(group.name)} />
      <ColorDot color={group.color} label={`${de.design.categoryColor}: ${group.name}`} onPick={(color) => void actions.updateGroup(group.id, { color })} />
      {editing ? (
        <input
          autoFocus
          className="cat-rename"
          value={name}
          aria-label={de.design.categoryName}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              e.stopPropagation();
              setName(group.name);
              setEditing(false);
            }
          }}
          data-testid="category-rename-input"
        />
      ) : (
        <button
          type="button"
          className="cat-title"
          onClick={onToggle}
          onDoubleClick={rename}
          onKeyDown={(e) => {
            if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
            e.preventDefault();
            drag.onMove(e.key === 'ArrowUp' ? -1 : 1);
          }}
          title={de.design.categoryHint}
          data-testid="category-title"
        >
          {group.name}
        </button>
      )}
      <span className="cat-count" data-testid="category-count">
        {count}
      </span>
      <span className="grow" />
      <span className="cat-tools">
        <button type="button" className="btn ghost small" onClick={onNew} title={de.design.newIn(newLabel, group.name)} data-testid="category-new">
          + {newLabel}
        </button>
        <button type="button" className="btn ghost icon small" onClick={rename} title={de.design.categoryRename} aria-label={`${group.name}: ${de.design.categoryRename}`} data-testid="category-rename">
          ✎
        </button>
        <button
          type="button"
          className="btn ghost icon small"
          onClick={() => void remove()}
          title={de.design.categoryDelete}
          aria-label={`${group.name}: ${de.design.categoryDelete}`}
          data-testid="category-delete"
        >
          ✕
        </button>
      </span>
    </header>
  );
}

/** Neue Kategorie direkt in der Kartenansicht: Name eingeben, Enter legt sie an (Esc bricht ab). */
function NewCategory({ onCreate, onCancel }: { onCreate: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const finished = useRef(false);
  const done = (): void => {
    if (finished.current) return;
    finished.current = true;
    if (name.trim()) onCreate(name.trim());
    else onCancel();
  };
  return (
    <section className="cat-section new" data-testid="category-new-row">
      <header className="cat-head">
        <span className="cat-dot static" aria-hidden="true" />
        <input
          autoFocus
          className="cat-rename"
          value={name}
          placeholder={de.design.categoryName}
          aria-label={de.design.categoryName}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          onBlur={done}
          onKeyDown={(e) => {
            if (e.key === 'Enter') done();
            if (e.key === 'Escape') {
              e.stopPropagation();
              finished.current = true;
              onCancel();
            }
          }}
          data-testid="category-new-name"
        />
      </header>
    </section>
  );
}

/** Bibliothek eines Bereichs: Karten (nach Kategorien) oder Pinnwand (Kategorien als Rahmen); oben eine Leiste zum Anlegen und für die Kartengröße. */
function FocusPanel({ focus }: { focus: Focus }) {
  const { ctx, story, actions } = useStoryEnv();
  const del = useDeleteActions();
  const ws = useWorkspace();
  const { moveInto, create: createCategory, reorder } = useCategoryActions();
  const [query, setQuery] = useState('');
  const [board, setBoardState] = useState(lastBoard);
  const setBoard = (on: boolean): void => {
    lastBoard = on;
    setBoardState(on);
  };
  const [size, setSize] = useCardSize();
  const [newCategory, setNewCategory] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  /** gezogene Kategorie und die Stelle, an der sie landen würde (vor bzw. nach dem Abschnitt) */
  const [catDrag, setCatDrag] = useState<string | null>(null);
  const [catOver, setCatOver] = useState<{ key: string; after: boolean } | null>(null);
  const cardsBox = useRef<HTMLDivElement>(null);
  /** Lage der Abschnitte vor dem Umsortieren: danach gleiten sie von dort an ihre neue Stelle */
  const flip = useRef<Map<string, number> | null>(null);
  /** Kategorie, deren Name nach dem Umsortieren mit der Tastatur wieder den Fokus bekommt */
  const refocus = useRef<string | null>(null);
  const [, setTick] = useState(0);
  const canvas = useRef<LibraryHandle>(null);
  const [fitTick, setFitTick] = useState(0);
  const [groupAt, setGroupAt] = useState<{ x: number; y: number } | null>(null);
  const type = focus.type;
  const sections = useMemo(() => (type ? categorySections(ctx, type.id, query) : { categories: [], rest: [] }), [ctx, type, query]);
  const all = useMemo(() => layoutLibrary(ctx), [ctx]);
  const placed = useMemo(() => (type ? all.filter((p) => p.entity.typeId === type.id) : []), [all, type]);
  const groups = useMemo(() => (type ? groupsOfType(ctx, type.id) : []), [ctx, type]);
  const selected = ws.selection?.kind === 'entity' || ws.selection?.kind === 'group' ? { kind: ws.selection.kind, id: ws.selection.id } : null;
  const newLabel = de.design.newTitle(type?.name ?? focus.builtinName ?? focus.label);
  const total = type ? story.entities.filter((e) => e.typeId === type.id).length : 0;
  const searching = query.trim() !== '';
  const cardsById = useMemo(() => new Map([...sections.categories.flatMap((c) => c.cards), ...sections.rest].map((c) => [c.entity.id, c])), [sections]);

  /** Klasse des Bereichs; ältere Projekte bekommen Gegenstand, Organisation, Konzept und Landschaft beim ersten Gebrauch. */
  const ensureType = async (): Promise<NodeType | null> => {
    if (type) return type;
    const key = ENTITY_OF[focus.key];
    const tpl = key ? templateFor(key) : null;
    return tpl ? ((await actions.createType(tpl as Parameters<typeof actions.createType>[0])) ?? null) : null;
  };
  const create = async (opts: { pos?: { x: number; y: number }; groupId?: string } = {}): Promise<void> => {
    const t = await ensureType();
    if (t) ws.openEntityDialog({ typeId: t.id, ...opts });
  };
  const open = (entityId: string): void => ws.select({ kind: 'entity', id: entityId });
  /** Neuer Eintrag auf der Pinnwand: an einer freien Stelle in der Bildmitte */
  const freeSpot = (): { x: number; y: number } => nearestFreePoint(placed.map((p) => ({ x: p.x, y: p.y })), canvas.current?.viewportCenter() ?? { x: 0, y: 0 });
  const addCategory = async (name: string): Promise<void> => {
    setNewCategory(false);
    const t = await ensureType();
    if (t) await createCategory(t.id, name);
  };

  // Ziehen und Ablegen der Karten zwischen den Abschnitten
  const dragProps = (card: EntityCard) => ({
    dragging: dragId === card.entity.id,
    onDragStart: (e: DragEvent): void => {
      e.dataTransfer.setData(ENTITY_MIME, card.entity.id);
      e.dataTransfer.effectAllowed = 'move';
      setDragId(card.entity.id);
    },
    onDragEnd: (): void => {
      setDragId(null);
      setOver(null);
    },
  });
  const dropTarget = (key: string, groupId: string | null) => ({
    onDragOver: (e: DragEvent): void => {
      if (!e.dataTransfer.types.includes(ENTITY_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      // immer setzen: ein „dragleave“ beim Wechsel zwischen inneren Elementen kann die Markierung gerade zurückgesetzt haben
      setOver(key);
    },
    onDragLeave: (e: DragEvent): void => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver((o) => (o === key ? null : o));
    },
    onDrop: (e: DragEvent): void => {
      const id = e.dataTransfer.getData(ENTITY_MIME);
      if (!id) return;
      e.preventDefault();
      setOver(null);
      setDragId(null);
      const card = cardsById.get(id);
      if (card) void moveInto(card.entity, groupId);
    },
  });
  // Umsortieren der Kategorien: Kopfzeile ziehen und vor bzw. nach einem Abschnitt ablegen (auf „Ohne Kategorie“: ans Ende)
  const order = sections.categories.map((c) => c.group.id);
  const orderKey = order.join(',');
  const remember = (): void => {
    const tops = new Map<string, number>();
    cardsBox.current?.querySelectorAll<HTMLElement>('[data-group-id]').forEach((el) => tops.set(el.dataset.groupId!, el.getBoundingClientRect().top));
    flip.current = tops;
  };
  const moveCategory = (id: string, beforeId: string | null): boolean => {
    if (!type || reorderGroups(groupsOfType(ctx, type.id), id, beforeId).length === 0) return false;
    remember();
    void reorder(type.id, id, beforeId);
    return true;
  };
  useLayoutEffect(() => {
    const before = flip.current;
    flip.current = null;
    // der Name behält den Fokus (React hat den Abschnitt dabei im Dokument verschoben)
    if (refocus.current) cardsBox.current?.querySelector<HTMLElement>(`[data-group-id="${refocus.current}"] [data-testid="category-title"]`)?.focus();
    refocus.current = null;
    if (!before || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    cardsBox.current?.querySelectorAll<HTMLElement>('[data-group-id]').forEach((el) => {
      const from = before.get(el.dataset.groupId!);
      const dy = from === undefined ? 0 : from - el.getBoundingClientRect().top;
      if (Math.abs(dy) > 0.5) el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
    });
  }, [orderKey]);
  const categoryDrag = (id: string): CategoryDrag => ({
    onDragStart: (e: DragEvent): void => {
      if ((e.target as HTMLElement).closest('input')) return;
      e.dataTransfer.setData(CATEGORY_MIME, id);
      e.dataTransfer.effectAllowed = 'move';
      setCatDrag(id);
    },
    onDragEnd: (): void => {
      setCatDrag(null);
      setCatOver(null);
    },
    onMove: (delta) => {
      const i = order.indexOf(id);
      if (i < 0 || i + delta < 0 || i + delta >= order.length) return;
      if (moveCategory(id, delta < 0 ? order[i - 1]! : (order[i + 2] ?? null))) refocus.current = id;
    },
  });
  /** Ablegen auf einem Abschnitt: Karten wie bisher, Kategorien vor bzw. nach ihm (je nach Hälfte) */
  const sectionTarget = (key: string, groupId: string | null) => {
    const cards = dropTarget(key, groupId);
    const half = (e: DragEvent): boolean => {
      const box = e.currentTarget.getBoundingClientRect();
      return groupId !== null && e.clientY > box.top + box.height / 2;
    };
    return {
      onDragOver: (e: DragEvent): void => {
        if (!e.dataTransfer.types.includes(CATEGORY_MIME)) return cards.onDragOver(e);
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const after = half(e);
        setCatOver((o) => (o?.key === key && o.after === after ? o : { key, after }));
      },
      onDragLeave: (e: DragEvent): void => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setCatOver((o) => (o?.key === key ? null : o));
        cards.onDragLeave(e);
      },
      onDrop: (e: DragEvent): void => {
        const id = e.dataTransfer.getData(CATEGORY_MIME);
        if (!id) return cards.onDrop(e);
        e.preventDefault();
        setCatOver(null);
        setCatDrag(null);
        moveCategory(id, groupId === null ? null : half(e) ? (order[order.indexOf(groupId) + 1] ?? null) : groupId);
      },
    };
  };
  const markOf = (key: string): string => (catOver?.key === key ? (catOver.after ? ' drop-after' : ' drop-before') : '');
  const cardsOf = (cards: EntityCard[]): ReactNode =>
    cards.map((c) => (
      <Card key={c.entity.id} card={c} color={focus.color} selected={selected?.kind === 'entity' && selected.id === c.entity.id} reference={referenceOf(ctx, c.entity.id)} onOpen={() => open(c.entity.id)} onDelete={() => void del.entity(c.entity)} {...dragProps(c)} />
    ));
  const addCard = (
    <button type="button" className="entity-card add" onClick={() => void create()} data-testid="design-new-card">
      <span className="ec-add-plus" aria-hidden="true">
        +
      </span>
      <span>{newLabel}</span>
    </button>
  );
  const withCategories = groups.length > 0 || newCategory;

  return (
    <section className="design-main" aria-label={focus.label} data-testid="design-focus-panel" data-focus={focus.key}>
      <header className="design-head">
        <span className="design-head-icon" style={{ color: focus.color }}>
          <FocusIcon focus={focus} size={26} />
        </span>
        <h1>{focus.label}</h1>
        <span className="design-count muted">{total}</span>
        <span className="grow" />
        <div className="seg" role="group" aria-label={de.design.display}>
          <button type="button" className={`seg-btn${board ? '' : ' active'}`} aria-pressed={!board} onClick={() => setBoard(false)} data-testid="design-grid">
            {de.design.grid}
          </button>
          <button type="button" className={`seg-btn${board ? ' active' : ''}`} aria-pressed={board} onClick={() => setBoard(true)} title={de.design.boardHint} data-testid="design-board">
            {de.design.board}
          </button>
        </div>
      </header>

      <div className="design-toolbar" role="toolbar" aria-label={de.design.toolbar} data-testid="design-toolbar">
        <button type="button" className="btn small primary" onClick={() => void create(board ? { pos: freeSpot() } : {})} data-testid="design-new">
          + {newLabel}
        </button>
        <button
          type="button"
          className="btn small"
          onClick={() => (board ? setGroupAt(canvas.current?.viewportCenter() ?? { x: 0, y: 0 }) : setNewCategory(true))}
          title={de.design.newCategoryHint}
          data-testid="add-group"
        >
          + {de.design.newCategory}
        </button>
        <span className="grow" />
        {!board && total > 0 && <input type="search" className="design-search" placeholder={de.design.search} aria-label={de.design.search} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="design-search" />}
        {!board && (
          <span className="size-ctl" role="group" aria-label={de.design.cardSize} data-size={size} data-testid="card-size">
            <button type="button" className="btn ghost icon small" onClick={() => setSize(size - 1)} disabled={size === 0} aria-label={de.design.smaller} title={de.design.smaller} data-testid="card-smaller">
              −
            </button>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
              <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
              <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
              <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
            </svg>
            <button type="button" className="btn ghost icon small" onClick={() => setSize(size + 1)} disabled={size === CARD_SIZES.length - 1} aria-label={de.design.bigger} title={de.design.bigger} data-testid="card-bigger">
              +
            </button>
          </span>
        )}
        {board && (
          <button type="button" className="btn small ghost" onClick={() => setFitTick((t) => t + 1)} title={de.toolbar.fit} data-testid="fit">
            ⤢ {de.toolbar.fit}
          </button>
        )}
      </div>

      {!board &&
        (total === 0 && !withCategories ? (
          <div className="design-empty" data-testid="design-empty">
            <span className="design-empty-icon" style={{ color: focus.color }}>
              <FocusIcon focus={focus} size={44} />
            </span>
            <p>{de.design.empty(focus.label)}</p>
            <button type="button" className="btn primary" onClick={() => void create()}>
              + {newLabel}
            </button>
          </div>
        ) : (
          <div ref={cardsBox} className={`design-cards size-${size}${dragId ? ' dragging' : ''}${catDrag ? ' cat-sorting' : ''}`} style={{ '--card-min': `${CARD_SIZES[size]}px` } as CSSProperties} data-testid="design-cards">
            {!withCategories ? (
              <div className="design-grid">
                {cardsOf(sections.rest)}
                {searching ? sections.rest.length === 0 && <div className="muted design-nohits">{de.design.noHits}</div> : addCard}
              </div>
            ) : (
              <>
                {sections.categories.map(({ group, cards }) => {
                  if (searching && cards.length === 0) return null;
                  const isOpen = searching || !closedCategories.has(group.id);
                  return (
                    <section
                      key={group.id}
                      className={`cat-section${over === group.id ? ' over' : ''}${catDrag === group.id ? ' cat-dragging' : ''}${markOf(group.id)}`}
                      style={{ '--cat': group.color } as CSSProperties}
                      aria-label={group.name}
                      data-testid="category"
                      data-group-id={group.id}
                      {...sectionTarget(group.id, group.id)}
                    >
                      <CategoryHead
                        group={group}
                        count={cards.length}
                        open={isOpen}
                        newLabel={newLabel}
                        onToggle={() => {
                          if (closedCategories.has(group.id)) closedCategories.delete(group.id);
                          else closedCategories.add(group.id);
                          setTick((t) => t + 1);
                        }}
                        onNew={() => void create({ groupId: group.id })}
                        drag={categoryDrag(group.id)}
                      />
                      {isOpen && (
                        <div className="design-grid">
                          {cardsOf(cards)}
                          {cards.length === 0 && <div className="cat-empty muted small">{de.design.categoryEmpty}</div>}
                        </div>
                      )}
                    </section>
                  );
                })}
                {newCategory && <NewCategory onCreate={(name) => void addCategory(name)} onCancel={() => setNewCategory(false)} />}
                {(!searching || sections.rest.length > 0 || dragId) && (
                  <section className={`cat-section rest${over === 'none' ? ' over' : ''}${markOf('none')}`} aria-label={de.design.noCategory} data-testid="category-none" {...sectionTarget('none', null)}>
                    <header className="cat-head">
                      <span className="cat-title static">{de.design.noCategory}</span>
                      <span className="cat-count">{sections.rest.length}</span>
                      {dragId && <span className="muted small">{de.design.restHint}</span>}
                    </header>
                    <div className="design-grid">
                      {cardsOf(sections.rest)}
                      {!searching && addCard}
                    </div>
                  </section>
                )}
                {searching && sections.categories.every((c) => c.cards.length === 0) && sections.rest.length === 0 && <div className="muted design-nohits">{de.design.noHits}</div>}
              </>
            )}
          </div>
        ))}

      {board && (
        <div className="design-board-wrap">
          <div className="canvas-wrap design-canvas" data-testid="library-view">
            <LibraryCanvas
              handleRef={canvas}
              ctx={ctx}
              placed={placed}
              groups={groups}
              selected={selected}
              fitTick={fitTick}
              onSelect={(sel) => ws.select(sel)}
              onAddAt={(pos) => void create({ pos })}
              onMoveEntity={(id, pos) => void actions.updateEntity(id, pos)}
              onMoveGroup={(id, pos, members) =>
                // Rahmen samt Karten: im Verlauf ein Schritt
                void actions.group(() => Promise.all([actions.updateGroup(id, pos), ...members.map((m) => actions.updateEntity(m.id, { x: m.x, y: m.y }))]))
              }
              onResizeGroup={(id, sz) => void actions.updateGroup(id, sz)}
            />
            <div className="canvas-overlay library-note muted small">{de.design.boardHint}</div>
          </div>
          {groupAt && type && <NewGroupDialog pos={groupAt} typeId={type.id} onClose={() => setGroupAt(null)} onCreated={() => setGroupAt(null)} />}
          {placed.length === 0 && (
            <div className="board-empty muted" role="note">
              {de.design.boardEmpty}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Welt: was für die ganze Geschichte gilt, direkt bearbeitbar und um Felder erweiterbar (die Prämisse steht hier nicht, die Welt hat die ganze Breite). */
function WorldPanel() {
  const { ctx, actions } = useStoryEnv();
  const list = ctx.foundationals.filter((f) => ctx.types.get(f.typeId)?.name !== 'Prämisse');
  return (
    <section className="design-main" aria-label={de.design.world} data-testid="design-world" data-focus="world">
      <header className="design-head">
        <span className="design-head-icon">
          <FocusIcon focus={{ key: 'world', color: '' }} size={26} />
        </span>
        <h1>{de.design.world}</h1>
      </header>
      <div className="design-world">
        <p className="muted design-intro">{de.design.worldIntro}</p>
        <div className="world-cards">
          {list.map((f) => {
            const type = ctx.types.get(f.typeId)!;
            return (
              <section key={f.id} className="world-card" aria-label={type.name} data-testid="foundational">
                <div className="row">
                  <span className="swatch" style={{ background: type.color }} aria-hidden="true" />
                  <h2>{type.name}</h2>
                </div>
                <FieldsForm
                  defs={type.fields}
                  values={f.fields}
                  onChange={(k, v) => void actions.updateEntity(f.id, { fields: { [k]: v } })}
                  idPrefix={`found-${type.name.toLowerCase()}`}
                  imageKey={type.imageKey}
                  fieldActions={(def) => <FieldMenu type={type} def={def} plural={null} />}
                />
                <FieldAdder type={type} />
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Modus „Bibliothek“: links die Bereiche (Personen, Orte, Gegenstände, Organisationen, Konzepte, eigene Klassen, Welt), rechts die
 * Einträge des gewählten Bereichs als Karten (nach Kategorien) oder auf der Pinnwand. Neue Einträge entstehen in einem eigenen Fenster.
 */
export function DesignView() {
  const { ctx } = useStoryEnv();
  const ws = useWorkspace();
  const foci = useMemo(() => designFoci(ctx), [ctx]);
  const focus = foci.find((f) => f.key === ws.designFocus) ?? foci[0]!;
  const [collapsed, toggle] = useRailCollapsed('design');
  const [importing, setImporting] = useState(false);
  const importIcon = iconNamed('import');
  return (
    <div className="design-view" data-testid="design-view">
      <SideRail
        title={de.design.focusLabel}
        collapsed={collapsed}
        onToggle={toggle}
        testId="design-rail"
        bottom={
          <RailItem
            icon={importIcon ? <MaskIcon url={importIcon} /> : <span aria-hidden="true">↓</span>}
            label={de.libraryImport.button}
            title={de.libraryImport.buttonHint}
            onClick={() => setImporting(true)}
            data-testid="library-import-open"
          />
        }
      >
        {foci.map((f) => (
          <RailItem
            key={f.key}
            icon={<FocusIcon focus={f} />}
            label={f.label}
            count={f.key === 'world' ? null : f.count}
            active={f.key === focus.key}
            separated={f.key === 'world'}
            aria-current={f.key === focus.key ? 'page' : undefined}
            onClick={() => ws.setDesignFocus(f.key)}
            data-testid={`focus-${f.key.startsWith('type:') ? 'custom' : f.key}`}
            data-focus={f.key}
          />
        ))}
      </SideRail>
      {focus.key === 'world' ? <WorldPanel /> : <FocusPanel key={focus.key} focus={focus} />}
      {importing && <LibraryImport onClose={() => setImporting(false)} />}
    </div>
  );
}
